import { useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import { useLocation } from 'react-router-dom';
import { FormBuilderContainer } from '../FormBuilderContainer';
import { FormBuilderSignature } from '../FormBuilderSignature';
import { formBuilderSignatureConfig } from '../constants';
import {
  ESignatureSettingsRequiredBy,
  ESignatureSettingsType,
  TFormBuilderContent,
  TFormBuilderEditSchemaCamel,
} from '@/types';
import { isValueOfEnum } from '@/utils/isValueOfEnum';

type TProps = {
  contentMode?: TFormBuilderContent;
};

/**
 * Signature settings block shown alongside the fields in a form builder.
 * @param props - Component props.
 * @param props.contentMode - Which builder this belongs to, deciding the
 * signature types offered and whether participants can be picked.
 * @returns The signature settings block.
 */
export const FormBuilderSignatureField = ({
  contentMode = 'formBuilder',
}: TProps) => {
  const { watch, setValue } = useFormContext<TFormBuilderEditSchemaCamel>();
  const signatureSettings = watch('signatureSettings');
  const { allowedTypes, withRequiredBy, defaultRequiredBy } =
    formBuilderSignatureConfig[contentMode];

  const { state } = useLocation();
  const userType = state?.userType;
  const fallbackRequiredBy = withRequiredBy
    ? userType === 'patient'
      ? ESignatureSettingsRequiredBy.PATIENT
      : ESignatureSettingsRequiredBy.PROVIDER
    : defaultRequiredBy;

  const handleSignatureTypeChange = useCallback(
    (value: string) => {
      if (!isValueOfEnum(value, ESignatureSettingsType)) return;
      setValue('signatureSettings.type', value);
      if (value !== ESignatureSettingsType.NO_SIGNATURE) return;
      setValue('signatureSettings.requiredBy', fallbackRequiredBy);
    },
    [setValue, fallbackRequiredBy],
  );

  const handleSignatureRequiredByChange = useCallback(
    (value: string) => {
      if (isValueOfEnum(value, ESignatureSettingsRequiredBy)) {
        setValue('signatureSettings.requiredBy', value);
      }
    },
    [setValue],
  );

  return (
    <FormBuilderContainer>
      <FormBuilderSignature
        userType={userType}
        type={signatureSettings.type}
        requiredBy={signatureSettings.requiredBy}
        onSignatureTypeChange={handleSignatureTypeChange}
        onSignatureRequiredByChange={handleSignatureRequiredByChange}
        allowedTypes={allowedTypes}
        withRequiredBy={withRequiredBy}
        withTitle
      />
    </FormBuilderContainer>
  );
};
